<template>
    <div
        class="textbox-container tw-flex tw-flex-col tw-gap-1 tw-text-gray-400 hover:tw-text-gray-500 focus:tw-text-gray-400 focus-within:tw-text-gray-600"
        :class="{
            'tw-w-full': expanded,
        }"
    >
        <div
            class="tw-flex tw-relative"
            :class="{
                'tw-w-full': expanded,
                'tw-flex-col': !!label && !$slots.before,
            }"
        >
            <template v-if="disabled || !toggle || isEditing">
                <slot name="before">
                    <label
                        v-if="label"
                        :for="id ?? 'textbox-' + $.uid"
                        class="tw-text-xs tw-font-medium tw-mb-1.5 tw-text-gray-700"
                    >
                        {{ label }}
                        <span v-if="required" class="tw-text-red-500">*</span>
                    </label>
                </slot>
                <input
                    v-if="type !== 'textarea'"
                    :id="id ?? 'textbox-' + $.uid"
                    ref="input"
                    v-model="modelValue"
                    :readonly
                    :autocomplete="autoComplete ? 'on' : 'off'"
                    class="tw-text-sm tw-border tw-border-gray-300 tw-bg-white tw-w-full tw-px-3.5 tw-py-1.5 tw-rounded-md tw-text-gray-900 tw-transition-all tw-duration-150 placeholder:tw-text-gray-400 hover:tw-border-gray-400 focus:tw-border-gray-600 focus:tw-outline-none"
                    :class="[
                        inputClass,
                        {
                            'is-compact': compact,
                            'tw-w-full': expanded,
                            'tw-py-1 tw-px-2.5 tw-text-xs': compact,
                            '!tw-bg-white/10 !tw-text-white !tw-border-white/30 placeholder:!tw-text-white/50 hover:!tw-bg-white/15 hover:!tw-border-white/50 focus:!tw-bg-white/15 focus:!tw-border-white/70':
                                glass,
                            '!tw-px-4 !tw-py-3 !tw-text-base': large,
                            '!tw-pl-9': icon,
                            'tw-border-none': borderless,
                            '!tw-border-red-300 hover:!tw-border-red-400 focus:!tw-border-red-500': hasError,
                            '!tw-border-emerald-300 hover:!tw-border-emerald-400 focus:!tw-border-emerald-500':
                                hasSuccess,
                            '!tw-bg-gray-100 !tw-text-gray-400 !tw-border-gray-200 hover:!tw-border-gray-200 !tw-cursor-not-allowed':
                                disabled,
                            '!tw-bg-gray-50 !tw-text-gray-500 hover:!tw-border-gray-300 focus:!tw-border-gray-300 !tw-cursor-default':
                                readonly && !disabled,
                            '!tw-pr-8': clearable && modelValue,
                        },
                    ]"
                    :aria-invalid="hasError ? 'true' : undefined"
                    :disabled
                    :type="type === 'currency' ? 'number' : type"
                    :maxlength="maxlength"
                    :min="min"
                    :max="max"
                    :placeholder="placeholder"
                    :required="required"
                    :step="step"
                    :name="name"
                    @input="handleInput"
                    @keypress="handleKeyPress"
                />
                <!-- Updated textarea focus state to match input styling -->
                <textarea
                    v-else-if="type === 'textarea'"
                    :id="id ?? 'textbox-' + $.uid"
                    ref="input"
                    v-model="modelValue"
                    :readonly
                    class="tw-text-sm tw-border tw-border-gray-300 tw-bg-white tw-w-full tw-px-3.5 tw-py-1.5 tw-rounded-md tw-text-gray-900 tw-transition-all tw-duration-150 placeholder:tw-text-gray-400 hover:tw-border-gray-400 focus:tw-border-gray-600 focus:tw-outline-none tw-resize-y"
                    :class="{
                        'is-compact': compact,
                        'tw-w-full': expanded,
                        'tw-py-1 tw-px-2.5 tw-text-xs': compact,
                        '!tw-bg-white/10 !tw-text-white !tw-border-white/30 placeholder:!tw-text-white/50 hover:!tw-bg-white/15 hover:!tw-border-white/50 focus:!tw-bg-white/15 focus:!tw-border-white/70':
                            glass,
                        'tw-px-4 tw-py-3 !tw-text-base': large,
                        '!tw-pl-9': icon,
                        'tw-border-none': borderless,
                        '!tw-border-red-300 hover:!tw-border-red-400 focus:!tw-border-red-500': hasError,
                        '!tw-border-emerald-300 hover:!tw-border-emerald-400 focus:!tw-border-emerald-500': hasSuccess,
                        '!tw-bg-gray-100 !tw-text-gray-400 !tw-border-gray-200 hover:!tw-border-gray-200 !tw-cursor-not-allowed':
                            disabled,
                        '!tw-bg-gray-50 !tw-text-gray-500 hover:!tw-border-gray-300 focus:!tw-border-gray-300 !tw-cursor-default':
                            readonly && !disabled,
                        '!tw-pr-8': clearable && modelValue,
                    }"
                    :aria-invalid="hasError ? 'true' : undefined"
                    :disabled
                    :rows="rows"
                    :maxlength="maxlength"
                    :name="name"
                    :placeholder="placeholder"
                    :required="required"
                    @input="handleInput"
                    @keypress="handleKeyPress"
                ></textarea>
                <i
                    v-if="icon"
                    :class="[
                        `mdi mdi-${icon} tw-absolute tw-top-1/2 tw-transform tw--translate-y-1/2 tw-left-2.5 tw-text-lg tw-text-gray-400 tw-pointer-events-none`,
                        {
                            '!tw-text-2xl !tw-left-3': large,
                            '!tw-text-white/60': glass,
                        },
                    ]"
                ></i>
                <button
                    v-if="clearable && modelValue && !disabled && !readonly"
                    type="button"
                    class="tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-right-2.5 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors tw-duration-150 tw-leading-none focus:tw-outline-none"
                    @click="clearInput"
                >
                    <i class="mdi mdi-close tw-text-base"></i>
                </button>
                <slot name="after"></slot>
            </template>
            <Button
                v-else
                :disabled="disabled"
                :class="{
                    'is-compact': compact,
                }"
                class="tw-text-eventwise-link tw-my-1"
                @click="toggleEdit()"
            >
                {{ formattedInputValue }}
                <i v-if="!modelValue" class="mdi mdi-pencil ml-1"></i>
            </Button>
        </div>
        <span v-if="validationError" class="tw-text-red-500 tw-font-avenir tw-text-xs" role="alert">
            {{ validationError }}
        </span>
    </div>
</template>
<script setup lang="ts">
import { localise } from '@/js/utils.js';
import Button from '@/js/components/controls/Button.vue';
import { computed, inject, ref, useTemplateRef, type PropType } from 'vue';

type TextboxValue = string | number | null;
type TextboxValidator = (value: TextboxValue) => string | null | undefined;

const error = inject('error', null);
const success = inject('success', null);
const input = useTemplateRef('input');

const props = defineProps({
    maxlength: {
        type: String,
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    toggle: {
        type: Boolean,
        required: false,
        default: false,
    },
    placeholder: {
        type: String,
        required: false,
        default: null,
    },
    required: {
        type: Boolean,
        required: false,
        default: false,
    },
    type: {
        type: String,
        required: false,
    },
    step: {
        type: String,
        required: false,
    },
    min: {
        type: [String, Number],
        required: false,
    },
    max: {
        type: [String, Number],
        required: false,
    },
    compact: {
        type: Boolean,
        default: false,
    },
    expanded: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: String,
        required: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    large: {
        type: Boolean,
        default: false,
    },
    name: {
        type: String,
        required: false,
    },
    rows: {
        type: String,
        required: false,
    },
    inputClass: {
        type: String,
        required: false,
    },
    clearable: {
        type: Boolean,
        default: false,
    },
    label: {
        type: String,
        default: undefined,
    },
    id: {
        type: String,
        default: undefined,
    },
    borderless: {
        type: Boolean,
        default: false,
    },
    glass: {
        type: Boolean,
        default: false,
    },
    validator: {
        type: Function as PropType<TextboxValidator>,
        required: false,
    },
    autoComplete: {
        type: Boolean,
        required: false,
        default: false,
    },
});
const modelValue = defineModel<TextboxValue>({
    type: [String, Number],
    required: false,
    default: null,
});
const emit = defineEmits(['toggled', 'updated']);
const isEditing = ref(false);
const hasInteracted = ref(false);
const validationError = computed(() => {
    if (!hasInteracted.value || typeof props.validator !== 'function') {
        return '';
    }

    const result = props.validator(modelValue.value);

    return typeof result === 'string' ? result : '';
});
const hasError = computed(() => validationError.value !== '' || Boolean(error));
const hasSuccess = computed(() => !hasError.value && Boolean(success));
const formattedInputValue = computed(() => {
    if (props.type === 'number') {
        const decimalPlaces = props.step ? (props.step.split('.')[1]?.length ?? 0) : 0;
        return localise(modelValue.value ? modelValue.value : 0, decimalPlaces, false);
    } else if (props.type === 'currency') {
        const decimalPlaces = props.step ? (props.step.split('.')[1]?.length ?? 0) : 2;
        return localise(modelValue.value ? modelValue.value : 0, decimalPlaces, true);
    }
    if (!modelValue.value) {
        return 'Click to set';
    }
    return modelValue.value;
});

const handleKeyPress = (e: KeyboardEvent) => {
    if (!props.toggle || !isEditing.value || e.code !== 'Enter') {
        return;
    }
    if ((props.type === 'textarea' && e.ctrlKey) || props.type !== 'textarea') {
        toggleEdit();
        if (modelValue.value !== input.value?.value) {
            emit('updated');
        }
    }
};
const toggleEdit = () => {
    if (props.disabled) {
        return;
    }
    isEditing.value = !isEditing.value;
    emit('toggled', isEditing.value);
    if (isEditing.value) {
        setTimeout(() => {
            focus();
        }, 50);
    }
};
const handleInput = () => {
    hasInteracted.value = true;
};
const clearInput = () => {
    hasInteracted.value = true;
    modelValue.value = '';
};
const focus = () => {
    input.value?.focus();
};

defineExpose({
    focus,
});
</script>

<style lang="scss" scoped>
@use '@/assets/sass/eventbooks/variables' as variables;
.textbox-container {
    display: flex;

    .input {
        &.is-compact {
            padding: 3px 5px 2px 5px;
            height: auto;
            width: auto;
        }
    }
}
</style>
